import React, { useEffect, useState } from 'react';
import { Button, FormGroup, Container, Row, Col, Card, CardBody, ButtonGroup } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import * as Icon from 'react-feather';
import { useFormik } from 'formik';
import * as Yup from 'yup';

// import authLogo from '../../assets/images/logos/collex-main-logo.png';
import authBg from '../../assets/images/background/auth-bg.png';
import { ReactComponent as LeftBg } from '../../assets/images/bg/login-bgleft.svg';
import { ReactComponent as RightBg } from '../../assets/images/bg/login-bg-right.svg';
import { ReactComponent as AuthLogo } from '../../assets/images/logos/Collex-main.svg';

import { decodeToken } from '../../utils/jwtUtils';
import { initializeSocket } from '../../config/Socket';
import FormInput from '../../components/form/FormInput';
import FormSelect from '../../components/form/FormSelect';
import { getFullDomain, languageOptions } from '../../utils/common';

import {
  followUpStatusUpdate,
  pauseModeUpdate,
  setPauseMode
} from '../../store/slices/callingSlice';
import { setIsDialNumber } from '../../store/slices/dialSlice';
import { authLogin, authTenantLogin } from '../../store/slices/authSlice';
import { missedFollowUpGet } from '../../store/slices/tenantSlices/dailyReportSlice';
import { useBrandingDetails } from '../../store/slices/adminSlices/brandingDetailSlice';
import { setCallingModalOpen, setLoginUserType, setTimezone } from '../../store/slices/commonSlice';

const Auth = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const brandingDetails = useBrandingDetails();
  const { i18n, t } = useTranslation();

  const [role, setRole] = useState('agent');

  const initialValues = {
    domain:
      process.env.REACT_APP_MODE === 'LOCAL' ? process.env.REACT_APP_LOCAL_DOMAIN : getFullDomain(),
    username: '',
    password: ''
  };

  const validationSchema = Yup.object().shape({
    username: Yup.string().required(t('usernameRequired')),
    password: Yup.string().required(t('passwordRequired'))
  });

  const getMissedFollowUp = async () => {
    try {
      const response = await dispatch(missedFollowUpGet()).unwrap();
      if (response && response?.data?.length > 0) {
        response?.data?.map((x) =>
          toast(
            ({ closeToast }) => (
              <div
                className="d-flex align-items-center gap-2 cursor-pointer"
                onClick={async () => {
                  try {
                    await dispatch(
                      followUpStatusUpdate({
                        follow_up_status: 1,
                        id: x?.id
                      })
                    ).unwrap();
                    dispatch(setIsDialNumber(x?.phone_number));
                    closeToast();
                  } catch (err) {
                    console.error('Failed to update follow-up status:', err);
                  }
                }}>
                <Icon.Clock size={30} className="text-white" />
                <div className="d-flex flex-column justify-content-start cursor-pointer w-100">
                  <div className="d-flex gap-3 align-items-center">
                    <strong
                      style={{
                        textTransform: 'capitalize',
                        fontWeight: 'bold',
                        marginBottom: '4px'
                      }}>
                      Follow Up
                    </strong>
                    <Icon.PhoneForwarded size={16} className="text-green" />
                  </div>
                  <div
                    style={{
                      fontSize: '13px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '250px'
                    }}>
                    {x?.phone_number}
                  </div>
                </div>
              </div>
            ),
            {
              className: 'custom-follow-up-toast',
              position: 'top-right',
              autoClose: false,
              hideProgressBar: true,
              closeOnClick: false,
              pauseOnHover: true,
              draggable: true
            }
          )
        );
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const onPauseModeChange = async (user) => {
    try {
      const payload = {
        user_id: user?.id,
        pause_mode:
          user?.process_details?.type === 'OutboundAndPredictive' ||
          user?.process_details?.type === 'BlendedAndPredictive'
            ? 1
            : 0
      };
      const response = await dispatch(pauseModeUpdate(payload)).unwrap();
      if (response && response?.success) {
        dispatch(setPauseMode(payload?.pause_mode));
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const onSubmit = async (values) => {
    try {
      const payload = {
        ...values
      };
      let data;
      if (role === 'agent') {
        data = await dispatch(authLogin(payload)).unwrap();
      } else {
        data = await dispatch(authTenantLogin(payload)).unwrap();
      }
      if (data && data?.success) {
        const user = decodeToken(data?.token);
        dispatch(setTimezone(user?.time_zone_details?.zone_name));
        dispatch(
          setLoginUserType(
            user?.type === 'TenantAdmin' || user?.type === 'supervisor' ? 'tenant' : 'agent'
          )
        );
        if (user?.type === 'TenantAdmin' || user?.type === 'supervisor') {
          navigate('/tenant-dashboard');
          initializeSocket();
        } else {
          initializeSocket();
          dispatch(setCallingModalOpen(user?.manualcall));
          getMissedFollowUp();
          if (process.env.REACT_APP_AGENT_PAUSE_MODE === 'true') {
            onPauseModeChange(user);
          }
          navigate('/dashboard');
        }
      } else {
        toast.error('Something went wrong');
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const { values, touched, errors, handleChange, handleSubmit, handleBlur } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  // Handle role change
  const handleToggle = (newRole) => {
    setRole(newRole);
  };

  useEffect(() => {
    localStorage.removeItem('timerStartTime');
    localStorage.removeItem('timerDuration');
  }, []);

  return (
    <div className="loginBox">
      <LeftBg className="position-absolute left bottom-0" />
      <RightBg className="position-absolute end-0 top" />
      {/* <div className="backgroundCircles">
        {[...Array(12)].map((_, index) => (
          <div key={index} className="circle"></div>
        ))}
      </div> */}
      <Container fluid className="h-100">
        <Row className="justify-content-center align-items-center h-100">
          <div className="lang-dropdown">
            <FormSelect
              // label={t('language')}
              isShowLabel={false}
              placeholder="Select language"
              options={languageOptions}
              name="language"
              value={i18n.resolvedLanguage || i18n.language}
              onChange={(e) => i18n.changeLanguage(e.target.value)}
            />
          </div>
          <Col lg="6" md="6" className="d-flex justify-content-center align-items-center">
            <img src={authBg} alt="auth-bg" className="w-75 user-select-none" />
          </Col>
          <Col lg="6" md="6" className="loginContainer">
            <Card>
              <div className="pt-4 d-flex align-items-center justify-content-center gap-2">
                {brandingDetails && brandingDetails?.headerlogo_path ? (
                  <img src={brandingDetails?.headerlogo_path} alt="logo" width={120} height={50} />
                ) : (
                  <AuthLogo width={120} height={50} />
                )}
              </div>
              <CardBody className="p-4 m-1">
                <ButtonGroup className="mb-4 w-100">
                  <Button
                    className="border-primary rounded-start-1 border-end-0"
                    color={role === 'agent' ? 'primary' : 'tansperent'}
                    onClick={() => handleToggle('agent')}>
                    {t('agent')}
                  </Button>
                  <Button
                    className="border-primary rounded-end-1 border-start-0"
                    color={role === 'admin' ? 'primary' : 'tansperent'}
                    onClick={() => handleToggle('admin')}>
                    {t('admin')}
                  </Button>
                </ButtonGroup>
                <form onSubmit={handleSubmit}>
                  <FormInput
                    name="username"
                    label={t('username')}
                    value={values.username}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                  <FormInput
                    name="password"
                    label={t('password')}
                    type="password"
                    value={values.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                  <FormGroup className="d-flex align-items-center justify-content-center w-100">
                    <Button type="submit" color="primary" className="me-2 w-100">
                      {t('login')}
                    </Button>
                  </FormGroup>
                </form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Auth;
